import { useState, useRef, useEffect, useCallback } from 'react'
import { X, User, Calendar, Clock, Play, Pause } from 'lucide-react'
import { formatters } from '../../../utils/formatters'
import type { Transcript } from '../../../types/transcript.types'

interface TranscriptDetailDrawerProps {
  transcript: Transcript | null
  onClose: () => void
}

export function TranscriptDetailDrawer({ transcript, onClose }: TranscriptDetailDrawerProps): JSX.Element {
  const isOpen = transcript !== null

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  const displayName = transcript?.username
    ?? (transcript?.device_user_id ? `User #${transcript.device_user_id}` : null)
  const formattedDate = transcript?.created_at
    ? formatters.formatDate(transcript.created_at)
    : null
  const metaDuration = formatters.formatDuration(transcript?.audio_duration ?? null)
  const confidenceText = formatters.formatConfidence(transcript?.confidence ?? null)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current.load()
    }
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [transcript?.id])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !transcript?.audio_url) return
    audio.src = transcript.audio_url
    const onLoadedMetadata = (): void => setDuration(audio.duration)
    const onTimeUpdate = (): void => setCurrentTime(audio.currentTime)
    const onEnded = (): void => { setIsPlaying(false); setCurrentTime(0) }
    const onError = (): void => setIsPlaying(false)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [transcript?.audio_url])

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) { audio.pause(); setIsPlaying(false) }
    else { try { await audio.play(); setIsPlaying(true) } catch { setIsPlaying(false) } }
  }, [isPlaying])

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    const time = parseFloat(e.target.value)
    audio.currentTime = time
    setCurrentTime(time)
  }, [])

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />
      )}
      <div
        className="fixed right-0 top-0 z-50 h-full w-full max-w-md overflow-y-auto border-l border-[#E5E7EB] bg-white transition-transform duration-300 ease-in-out"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)', willChange: 'transform' }}
        role="dialog" aria-modal="true" aria-labelledby="drawer-title"
      >
        <audio ref={audioRef} preload="metadata" />

        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-5">
          <h2 id="drawer-title" className="text-[18px] font-bold text-gray-900">Transcript Details</h2>
          <button onClick={onClose} className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700" aria-label="Close drawer" type="button">
            <X size={20} />
          </button>
        </div>

        {transcript && (
          <div className="space-y-5 p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-gray-900">Voice Transmission</h3>
              {confidenceText && (
                <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                  {confidenceText} confidence
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 text-[13px] text-gray-500">
              {displayName && (<div className="flex items-center gap-1.5"><User size={14} /><span>{displayName}</span></div>)}
              {formattedDate && (<div className="flex items-center gap-1.5"><Calendar size={14} /><span>{formattedDate}</span></div>)}
              <div className="flex items-center gap-1.5"><Clock size={14} /><span>{metaDuration}</span></div>
            </div>

            {transcript.audio_url ? (
              <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
                <div className="flex items-center gap-3">
                  <button onClick={togglePlay} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-green transition-opacity hover:opacity-90" aria-label={isPlaying ? 'Pause' : 'Play'} type="button">
                    {isPlaying ? <Pause size={18} className="text-black" /> : <Play size={18} className="ml-0.5 text-black" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <input type="range" min={0} max={duration || 0} step={0.1} value={currentTime} onChange={handleSeek}
                      className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-green" />
                    <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                      <span>{formatters.formatDuration(currentTime)}</span>
                      <span>{formatters.formatDuration(duration)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 text-center text-[13px] text-gray-500">
                Audio not available for this transcript
              </div>
            )}

            <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
              <p className="text-[14px] italic leading-relaxed text-gray-700">
                &ldquo;{transcript.transcript_text}&rdquo;
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
